import { IUserState, IUserStateCallback } from '../interface/userstate';

interface ICallbackRecord<T> {
    name: string;
    callback: IUserStateCallback<T>;
}

interface IUserStatePrv extends IUserState {
    name: string;
    collection: Record<string, any>;
    callbacks: Record<string, Array<ICallbackRecord<any>>>;
}

interface IUserStateSaveStructure {
    name: string;
    collection: Record<string, any>;
}

const StoreSymbol = Symbol.for('user-state');

/* eslint-disable no-restricted-globals */
const globalObject: any = typeof window === 'undefined' ? globalThis : window;
/* eslint-enable no-restricted-globals */

function _saveUserState(key: string, state: IUserStatePrv) {
    const store: Record<string, IUserStatePrv> = globalObject[StoreSymbol] ?? {};
    store[key] = state;
    globalObject[StoreSymbol] = store;
}

function _loadFromStore(name: string): IUserStatePrv | null {
    const store: Record<string, IUserStatePrv> = globalObject[StoreSymbol] ?? {};
    const val = store[name];
    return val;
}

function _GetValue(state: IUserStatePrv, key: string): any | undefined {
    return state.collection[key];
}

function _SetValue(state: IUserStatePrv, key: string, value: any | undefined) {
    if (value === undefined) {
        delete state.collection[key];
    } else {
        state.collection[key] = value;
    }
    _saveUserState(state.name, state);
}

function _init(name: string, startingData: Record<string, any>): IUserState {
    const val: IUserStatePrv = {
        name,
        collection: startingData,
        callbacks: {},
        getProperty: function <T>(this: IUserStatePrv, key: string) {
            return _GetValue(this, key) as T | undefined;
        },
        getPropertyWithDefault: function <T>(this: IUserStatePrv, key: string, defaultValue: T) {
            return (_GetValue(this, key) as T | undefined) ?? defaultValue;
        },
        setProperty: function (this: IUserStatePrv, key: string, value: any | undefined) {
            _SetValue(this, key, value);
            const cbArray = this.callbacks[key] ?? [];

            for (const record of cbArray) {
                record.callback(key, value);
            }
        },
        subscribeChanges: function <T>(
            this: IUserStatePrv,
            key: string,
            callback: IUserStateCallback<T>,
            subscriberKey?: string,
        ): void {
            const arr = this.callbacks[key] ?? [];
            const subKey = subscriberKey ?? `${key}-${arr.length}`;

            const record: ICallbackRecord<T> = {
                name: subKey,
                callback,
            };

            arr.push(record);

            this.callbacks[key] = arr;
        },
        unsubscribeChanges: function <T>(
            this: IUserStatePrv,
            key: string,
            callback: IUserStateCallback<T>,
        ): void {
            const arr = this.callbacks[key] ?? [];
            this.callbacks[key] = arr.filter(record => callback !== record.callback);
        },
        subscriber: function <T>(
            this: IUserStatePrv,
            subscriberKey: string,
        ): IUserStateCallback<T> | undefined {
            for (const key of Object.keys(this.callbacks)) {
                const records = this.callbacks[key];
                const match = records.filter(record => record.name === subscriberKey);

                if (match.length > 0) {
                    return match[0].callback;
                }
            }
            return undefined;
        },
        flatten: function (this: IUserStatePrv): string {
            const data: IUserStateSaveStructure = {
                name: this.name,
                collection: this.collection,
            };
            return JSON.stringify(data);
        },
    };

    return val as IUserState;
}

function load(name: string): IUserState {
    return _loadFromStore(name) ?? _init(name, {});
}

function expand(data: string): IUserState {
    const dataObj: IUserStateSaveStructure = data.length > 2 ? JSON.parse(data) : {};
    return _init(dataObj.name, dataObj.collection);
}

const module = {
    load,
    expand,
};

export default module;
